import {
  Badge,
  BasePagination,
  Button,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import {
  ICrmAccessControlData,
  useGetCrmAccessControlApi,
} from 'apis/crm-access-control-management/use-get-crm-access-control-api';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiPlus, FiTrash2, FiUser } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { GrantAccessModal } from '../components/modals/grant-access-modal';
import { RevokeAccessModal } from '../components/modals/revoke-access-modal';

// Components for table cells
const UserInfoCell: React.FC<{
  name: string;
  email: string;
  phoneNumber: string;
}> = ({ name, email, phoneNumber }) => (
  <div style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <HStack spacing={3}>
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: '#f7fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <FiUser size={20} color='#718096' />
      </div>
      <VStack align='start' spacing={1}>
        <Text fontWeight='semibold' fontSize='md' color='gray.800' lineHeight='1.2'>
          {name}
        </Text>
        <Text fontSize='sm' color='gray.600'>
          {email}
        </Text>
        <Text fontSize='sm' color='gray.500'>
          {phoneNumber}
        </Text>
      </VStack>
    </HStack>
  </div>
);

const RoleCell: React.FC<{ role: { id: number; name: string } }> = ({ role }) => (
  <div style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <Badge colorScheme='blue' variant='subtle' size='md' borderRadius='md'>
      {role.name}
    </Badge>
  </div>
);

const SupplierCell: React.FC<{ supplier: { id: number; name: string } }> = ({ supplier }) => (
  <div style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <Text fontWeight='medium' fontSize='md' color='gray.800'>
      {supplier.name}
    </Text>
  </div>
);

const CreatedAtCell: React.FC<{ createdAt: string }> = ({ createdAt }) => (
  <div style={{ height: '60px', display: 'flex', alignItems: 'center', padding: '8px 0' }}>
    <Text fontSize='sm' color='gray.600'>
      {new Date(createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}
    </Text>
  </div>
);

const ActionsCell: React.FC<{
  userInfo: ICrmAccessControlData;
  onRevokeAccess: (userInfo: ICrmAccessControlData) => void;
}> = ({ userInfo, onRevokeAccess }) => {
  return (
    <div
      style={{
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
        gap: '8px',
      }}>
      <Tooltip label={`Revoke access for ${userInfo.name}`}>
        <IconButton
          aria-label={`Revoke access for ${userInfo.name}`}
          icon={<FiTrash2 />}
          size='sm'
          colorScheme='red'
          variant='outline'
          onClick={() => onRevokeAccess(userInfo)}
        />
      </Tooltip>
    </div>
  );
};

const CrmAccessControlManagementListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();

  // API hooks
  const { isLoading, isFetching, data } = useGetCrmAccessControlApi();
  const { pageCount, total, data: accessControlData } = data ?? {};

  // Modal state
  const { isOpen: isGrantOpen, onOpen: onGrantOpen, onClose: onGrantClose } = useDisclosure();
  const { isOpen: isRevokeOpen, onOpen: onRevokeOpen, onClose: onRevokeClose } = useDisclosure();
  const [selectedUserForRevoke, setSelectedUserForRevoke] = useState<ICrmAccessControlData | null>(null);

  const handleRevokeAccess = (userInfo: ICrmAccessControlData) => {
    setSelectedUserForRevoke(userInfo);
    onRevokeOpen();
  };

  const handleRevokeClose = () => {
    setSelectedUserForRevoke(null);
    onRevokeClose();
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM Access Control Management'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='User Info' sortButtonComponent={<BaseSortButton sortBy='name' />} />,
        accessor: 'userInfo',
        width: '35%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={UserInfoCell} {...value} />,
      },
      {
        Header: <TableHeader name='Role' />,
        accessor: 'role',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={RoleCell} {...value} />,
      },
      {
        Header: <TableHeader name='Supplier' />,
        accessor: 'supplier',
        width: '25%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={SupplierCell} {...value} />,
      },
      {
        Header: <TableHeader name='Created At' />,
        accessor: 'createdAt',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={CreatedAtCell} {...value} />,
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '10%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      accessControlData?.map((item: ICrmAccessControlData) => ({
        id: item.id,
        userInfo: {
          name: item.name,
          email: item.email,
          phoneNumber: item.phoneNumber,
        },
        role: {
          role: item.role,
        },
        supplier: {
          supplier: item.supplier,
        },
        createdAt: {
          createdAt: item.createdAt,
        },
        actions: {
          userInfo: item,
          onRevokeAccess: handleRevokeAccess,
        },
      })) || [],
    [accessControlData]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='Access Control Management' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <HStack spacing={2}>
              <Button leftIcon={<FiPlus />} colorScheme='green' size='sm' onClick={onGrantOpen}>
                Grant Access
              </Button>
              <ClearFilterButton />
            </HStack>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={tableData} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {/* Modals */}
      <GrantAccessModal isOpen={isGrantOpen} onClose={onGrantClose} />

      {selectedUserForRevoke && (
        <RevokeAccessModal
          isOpen={isRevokeOpen}
          onClose={handleRevokeClose}
          userInfo={{
            id: selectedUserForRevoke.id,
            name: selectedUserForRevoke.name,
            email: selectedUserForRevoke.email,
            phoneNumber: selectedUserForRevoke.phoneNumber,
            role: selectedUserForRevoke.role,
            supplier: selectedUserForRevoke.supplier,
          }}
        />
      )}
    </>
  );
};

export default CrmAccessControlManagementListScreen;
